import "./index.css";
import type { ChangeEvent, ReactNode } from "react";
import { useId } from "react";
import type { IRadioProps } from "./types";

export const Radio = (props: IRadioProps) => {
  const {
    name,
    value,
    checked,
    onChange,
    label,
    description,
    accessory,
    radioPosition = "start",
    disabled = false,
    className,
    id,
    style,
    isCard = false,
    renderRadio,
    ...inputProps
  } = props;

  const fallbackId = useId();
  const radioId = id || fallbackId;

  const rootClassName = [
    "nuco-radio",
    "nuco-color-brand",
    radioPosition === "end" && "nuco-radio--reverse",
    disabled && "nuco-radio--disabled",
    isCard && "nuco-radio--card",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onChange(value);
    }
  };

  // Only ids of elements the kit renders: a custom `renderRadio` owns its content.
  const descriptionId =
    description && !renderRadio ? `${radioId}__desc` : undefined;
  const accessoryId = accessory ? `${radioId}-accessory` : undefined;
  const describedBy =
    [inputProps["aria-describedby"], descriptionId, accessoryId]
      .filter(Boolean)
      .join(" ") || undefined;

  let content: ReactNode;
  if (renderRadio) {
    const { renderRadio: _, ...restProps } = props;
    content = renderRadio({ ...restProps, radioId });
  } else {
    content = (label || description) && (
      <div className="nuco-radio__content">
        {label && (
          <div className="nuco-radio__label-wrapper">
            <span className="nuco-radio__label nuco-text nuco-text--md">
              {label}
            </span>
            {accessory && (
              <div
                id={accessoryId}
                className="nuco-radio__accessory"
                role="group"
              >
                {accessory}
              </div>
            )}
          </div>
        )}
        {description && (
          <div
            id={descriptionId}
            className="nuco-radio__description nuco-text nuco-text--md"
          >
            {description}
          </div>
        )}
      </div>
    );
  }

  return (
    <label
      className={rootClassName}
      htmlFor={radioId}
      data-iscard={isCard}
      style={style}
    >
      <input
        {...inputProps}
        id={radioId}
        type="radio"
        name={name}
        value={value}
        className="nuco-radio__input"
        tabIndex={disabled ? -1 : 0}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-label={
          !label && typeof inputProps["aria-label"] === "string"
            ? inputProps["aria-label"]
            : undefined
        }
        aria-describedby={describedBy}
      />

      {!renderRadio && <div className="nuco-radio__circle" />}

      {content}
    </label>
  );
};
